import React, { useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaCalendarAlt,
  FaCalendarWeek,
  FaChevronLeft,
  FaChevronRight,
  FaPlus,
  FaSearch,
  FaStepBackward,
  FaStepForward,
} from 'react-icons/fa';
import U2000ActionBtn from '../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000InputText from '../../../u2000Core/components/formControls/input/U2000InputText';
import u2000FormState, {
  U2000FormStateEnumEditMode,
  U2000FormStateEnumState,
  type U2000FormState,
} from '../../../u2000Core/reducer/formReducer/U2000FormState';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS } from '../../U3301';
import type { U3301InitLUCategory } from '../../U3301Service';
import U3301CategoryFilterDropdown from './U3301CategoryFilterDropdown';

type CalendarView = 'dayGridMonth' | 'dayGridWeek';

type SearchFormRow = { searchText: string };

interface U3301AgendaToolbarProps {
  view: CalendarView;
  onViewChange: (view: CalendarView) => void;
  onToday: () => void;
  onPrev: () => void;
  onNext: () => void;
  periodTitle: string;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  onSearchSubmit: (searchText: string) => void;
  onSearchNavigateClear: () => void;
  onSearchPrevOccurrence: () => void;
  onSearchNextOccurrence: () => void;
  searchNavigateActive: boolean;
  hasPreviousOccurrence: boolean;
  hasNextOccurrence: boolean;
  searchOccurrenceIndex: number;
  searchOccurrenceTotal: number;
  categoryIds: string[];
  onCategoryIdsChange: (ids: string[]) => void;
  categories: U3301InitLUCategory[];
  onCreateClick?: () => void;
}

/** Même hauteur que `U2000InputText` (px-4 py-3 text-sm + bordure ≈ 46px). */
const toolbarControlH = 'h-[46px]! min-h-[46px]! box-border! py-0!';
const iconBtnClass = `${toolbarControlH} w-[46px]! px-0! [&>svg]:mr-0!`;
const compactBtnClass = `${toolbarControlH} px-3!`;

const U3301AgendaToolbar: React.FC<U3301AgendaToolbarProps> = ({
  view,
  onViewChange,
  onToday,
  onPrev,
  onNext,
  periodTitle,
  searchText,
  onSearchTextChange,
  onSearchSubmit,
  onSearchNavigateClear,
  onSearchPrevOccurrence,
  onSearchNextOccurrence,
  searchNavigateActive,
  hasPreviousOccurrence,
  hasNextOccurrence,
  searchOccurrenceIndex,
  searchOccurrenceTotal,
  categoryIds,
  onCategoryIdsChange,
  categories,
  onCreateClick,
}) => {
  const { t } = useTranslation();
  const lastEmittedSearchRef = useRef(searchText);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchExpanded, setSearchExpanded] = useState(() => searchText.trim().length > 0);

  const [searchFormState, searchFormDispatch] = useReducer(
    u2000FormState,
    searchText,
    (initial): U2000FormState<SearchFormRow> => ({
      ...u2000IntFormState,
      state: U2000FormStateEnumState.EDIT,
      editMode: U2000FormStateEnumEditMode.EDIT,
      current: { searchText: initial },
      old: { searchText: initial },
    }),
  );

  const searchFormHandlers = useU2000FormStateHandlers<SearchFormRow>({
    formState: searchFormState,
    formStateDispatch: searchFormDispatch,
  });

  useEffect(() => {
    const current = searchFormState.current?.searchText ?? '';
    if (current === searchText) return;
    searchFormHandlers.setData({ searchText });
    lastEmittedSearchRef.current = searchText;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync parent → form sur searchText uniquement
  }, [searchText]);

  useEffect(() => {
    const next = searchFormState.current?.searchText ?? '';
    if (next === lastEmittedSearchRef.current) return;
    lastEmittedSearchRef.current = next;
    onSearchTextChange(next);
    if (next.trim() === '') {
      onSearchNavigateClear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- émettre seulement si la valeur change
  }, [searchFormState.current?.searchText]);

  useEffect(() => {
    if (!searchExpanded) return;
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, [searchExpanded]);

  const collapseSearchIfEmpty = () => {
    const value = (searchFormState.current?.searchText ?? '').trim();
    if (value === '') {
      setSearchExpanded(false);
    }
  };

  const clearAndCollapseSearch = () => {
    searchFormHandlers.setData({ searchText: '' });
    onSearchTextChange('');
    onSearchNavigateClear();
    setSearchExpanded(false);
  };

  const submitSearch = () => {
    const value = (searchFormState.current?.searchText ?? '').trim();
    if (value === '') return;
    if (value !== searchText) {
      onSearchTextChange(value);
    }
    onSearchSubmit(value);
  };

  return (
    <div className="flex shrink-0 flex-col gap-2">
      {/* Une ligne : gauche (nav + titre) · centre (vues) · droite (filtre + actions) */}
      <div className="grid min-w-0 grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <U2000ActionBtn
            label={<span className="sr-only">{t('btnPrev', { ns: NS })}</span>}
            handleClick={onPrev}
            variant="secondary"
            icon={FaChevronLeft}
            title={t('btnPrev', { ns: NS })}
            className={iconBtnClass}
          />
          <U2000ActionBtn
            label={t('btnToday', { ns: NS })}
            handleClick={onToday}
            variant="secondary"
            className={compactBtnClass}
          />
          <U2000ActionBtn
            label={<span className="sr-only">{t('btnNext', { ns: NS })}</span>}
            handleClick={onNext}
            variant="secondary"
            icon={FaChevronRight}
            title={t('btnNext', { ns: NS })}
            className={iconBtnClass}
          />
          <h2 className="min-w-0 truncate text-lg font-semibold text-gray-900 first-letter:uppercase">
            {periodTitle}
          </h2>
        </div>

        <div className="flex items-center justify-center gap-2">
          <U2000ActionBtn
            label={t('btnMonth', { ns: NS })}
            handleClick={() => onViewChange('dayGridMonth')}
            variant={view === 'dayGridMonth' ? 'primary' : 'secondary'}
            icon={FaCalendarAlt}
            className={compactBtnClass}
          />
          <U2000ActionBtn
            label={t('btnWeek', { ns: NS })}
            handleClick={() => onViewChange('dayGridWeek')}
            variant={view === 'dayGridWeek' ? 'primary' : 'secondary'}
            icon={FaCalendarWeek}
            className={compactBtnClass}
          />
        </div>

        <div className="flex items-center justify-end gap-2">
          <U3301CategoryFilterDropdown
            categories={categories}
            categoryIds={categoryIds}
            onCategoryIdsChange={onCategoryIdsChange}
          />
          {onCreateClick && (
            <U2000ActionBtn
              label={t('btnCreateEvent', { ns: NS })}
              handleClick={onCreateClick}
              variant="primary"
              icon={FaPlus}
              className={compactBtnClass}
            />
          )}

          {searchExpanded ? (
            <div className="flex items-center gap-1">
              <U2000ActionBtn
                label={<span className="sr-only">{t('btnSearchPrevOccurrence', { ns: NS })}</span>}
                handleClick={onSearchPrevOccurrence}
                variant="secondary"
                icon={FaStepBackward}
                title={t('btnSearchPrevOccurrence', { ns: NS })}
                className={iconBtnClass}
                disabled={!searchNavigateActive || !hasPreviousOccurrence}
              />
              <div
                className="h-[46px] w-56 shrink-0 [&_.space-y-1\.5]:space-y-0! [&_input]:box-border! [&_input]:h-[46px]! [&_input]:py-0!"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    submitSearch();
                    return;
                  }
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    if ((searchFormState.current?.searchText ?? '').trim() === '') {
                      setSearchExpanded(false);
                      onSearchNavigateClear();
                    } else {
                      clearAndCollapseSearch();
                    }
                  }
                }}
              >
                <U2000InputText
                  formStateHandlers={searchFormHandlers}
                  fieldName="searchText"
                  label={t('lblSearchText', { ns: NS })}
                  hideLabel
                  placeholder={t('lblSearchTextPlaceholder', { ns: NS })}
                  icon={<FaSearch className="h-3.5 w-3.5" aria-hidden />}
                  inputRef={searchInputRef as import('react').RefObject<HTMLInputElement>}
                  onBlur={collapseSearchIfEmpty}
                />
              </div>
              <U2000ActionBtn
                label={<span className="sr-only">{t('btnSearchSubmit', { ns: NS })}</span>}
                handleClick={submitSearch}
                variant="secondary"
                icon={FaSearch}
                title={t('btnSearchSubmit', { ns: NS })}
                className={iconBtnClass}
                disabled={(searchFormState.current?.searchText ?? '').trim() === ''}
              />
              {searchNavigateActive && searchOccurrenceTotal > 0 && (
                <span
                  className="tabular-nums shrink-0 px-1 text-sm font-medium text-gray-700"
                  title={t('msgSearchOccurrencePosition', {
                    ns: NS,
                    current: searchOccurrenceIndex,
                    total: searchOccurrenceTotal,
                  })}
                  aria-live="polite"
                >
                  {t('msgSearchOccurrencePosition', {
                    ns: NS,
                    current: searchOccurrenceIndex,
                    total: searchOccurrenceTotal,
                  })}
                </span>
              )}
              <U2000ActionBtn
                label={<span className="sr-only">{t('btnSearchNextOccurrence', { ns: NS })}</span>}
                handleClick={onSearchNextOccurrence}
                variant="secondary"
                icon={FaStepForward}
                title={t('btnSearchNextOccurrence', { ns: NS })}
                className={iconBtnClass}
                disabled={!searchNavigateActive || !hasNextOccurrence}
              />
            </div>
          ) : (
            <U2000ActionBtn
              label={<span className="sr-only">{t('lblSearchText', { ns: NS })}</span>}
              handleClick={() => setSearchExpanded(true)}
              variant="secondary"
              icon={FaSearch}
              title={t('lblSearchText', { ns: NS })}
              className={iconBtnClass}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default U3301AgendaToolbar;
